import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { NotificationService } from '../../services/notification-service/notification.service';
import { SessionExpiredError } from '../session/session.service';

/**
 * HTTP Error Interceptor
 *
 * Automatically intercepts and handles all HTTP errors in the application.
 * Provides consistent error handling, retry logic, and user feedback.
 *
 * Features:
 * - Automatic retry for temporary errors (5xx)
 * - Specific handling for different status codes (401, 403, 404, 500)
 * - Automatic logout on session timeout (401)
 * - Network error detection
 * - Consistent user notifications
 *
 * Usage:
 * Automatically registered in CoreModule as HTTP_INTERCEPTOR.
 * No manual integration needed - intercepts all HTTP requests automatically.
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private authService: AuthService,
    private notificationService: NotificationService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Branding-Konfiguration ist optional – 404 ist ein erwarteter Normalfall
    // und soll weder geloggt noch als Notification angezeigt werden.
    if (req.url.includes('branding.json')) {
      return next.handle(req);
    }

    // Hilfeseiten sind optional – fehlt ein Eintrag (404), zeigt die Komponente
    // selbst einen Hinweis. Kein Error-Toast nötig.
    if (req.url.includes('/knowledge/')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      // Retry logic: retry once for server errors (5xx)
      retry({
        count: 1,
        delay: (error: HttpErrorResponse) => {
          // Only retry for server errors (5xx)
          // Don't retry for client errors (4xx) or auth errors
          if (error.status === 401 || error.status === 403 || error.status < 500) {
            throw error;
          }
          return throwError(() => error);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        let errorMessage = '';

        if (error.error instanceof ErrorEvent) {
          // Client-side or network error
          errorMessage = `Network Error: ${error.error.message}`;
          this.handleNetworkError(error);
        } else {
          // Backend returned an unsuccessful response code
          errorMessage = `Server Error ${error.status}: ${error.message}`;
          this.handleBackendError(error);
        }

        // Log error for debugging
        console.error('HTTP Error:', errorMessage, error);

        // Re-throw the error so components can still handle it if needed
        return throwError(() => error);
      })
    );
  }

  private handleNetworkError(error: HttpErrorResponse): void {
    if (!navigator.onLine) {
      this.notificationService.showError('ERROR.NO_CONNECTION', 'Keine Internetverbindung');
    } else {
      this.notificationService.showError('ERROR.NETWORK_ERROR', 'Netzwerkfehler aufgetreten');
    }
  }

  private handleBackendError(error: HttpErrorResponse): void {
    switch (error.status) {
      case 401:
        this.handle401Error(error);
        break;
      case 403:
        this.handle403Error(error);
        break;
      case 404:
        this.handle404Error(error);
        break;
      case 500:
      case 502:
      case 503:
        this.handle5xxError(error);
        break;
      default:
        this.handleGenericError(error);
    }
  }

  private handle401Error(error: HttpErrorResponse): void {
    // DOCU-1323: Neuanmeldung abgebrochen, Request nicht gesendet. Nicht
    // abmelden — sonst sind die Eingaben auf der Seite weg.
    if (error instanceof SessionExpiredError) {
      this.notificationService.showError('ERROR.SESSION_EXPIRED_UNSAVED');
      return;
    }

    // TokenInterceptor hat bereits erneuert und wiederholt — der Token ist
    // wirklich ungueltig.
    this.notificationService.showError('ERROR.SESSION_TIMEOUT', 'Sitzung abgelaufen');

    // Logout and redirect to login
    this.authService.logout({
      logoutParams: { returnTo: window.location.origin }
    });
  }

  private handle403Error(error: HttpErrorResponse): void {
    // Forbidden - user doesn't have permission
    this.notificationService.showError('ERROR.FORBIDDEN', 'Zugriff verweigert');
  }

  private handle404Error(error: HttpErrorResponse): void {
    // Resource not found
    const message = error.error?.langKey || 'ERROR.NOT_FOUND';
    this.notificationService.showError(message, 'Ressource nicht gefunden');
  }

  private handle5xxError(error: HttpErrorResponse): void {
    // Server error
    this.notificationService.showError('ERROR.SERVER_ERROR', 'Serverfehler aufgetreten');
  }

  private handleGenericError(error: HttpErrorResponse): void {
    // Generic error handling
    const message = error.error?.langKey || 'ERROR.UNKNOWN';
    this.notificationService.showError(message);
  }
}
